import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle2, Compass, Target, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import WhatsAppConfirmDialog from './WhatsAppConfirmDialog';
import { getWhatsAppLink } from './WhatsAppButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CareerConsultation = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    currentStatus: '',
    interestedDomain: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [whatsAppLink, setWhatsAppLink] = useState('');

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const link = getWhatsAppLink('career-consultation', formData);
      const response = await axios.post(`${BACKEND_URL}/api/career-consultation`, formData);
      if (response.data.success) {
        toast.success('Thank you! We will contact you soon.');
        setWhatsAppLink(link);
        setShowWhatsApp(true);
        setFormData({
          name: '',
          email: '',
          mobile: '',
          currentStatus: '',
          interestedDomain: '',
          message: '',
        });
      }
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <WhatsAppConfirmDialog
      isOpen={showWhatsApp}
      onClose={setShowWhatsApp}
      whatsAppLink={whatsAppLink}
      formType="career-consultation"
    />
    <section id="career-consultation" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#111827]">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Side - Info */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold mb-4 font-['Space_Grotesk']">
                Free <span className="bg-gradient-to-r from-[#00E5FF] to-[#1DE9B6] bg-clip-text text-transparent">Career Consultation</span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mb-4"></div>
              <p className="text-lg text-[#9CA3AF] leading-relaxed">
                Get personalized guidance to accelerate your tech career journey
              </p>
            </div>

            <div className="space-y-6">
              {[
                { icon: Compass, text: 'Career roadmap guidance' },
                { icon: Target, text: 'Skill & role alignment' },
                { icon: TrendingUp, text: 'Industry insights' },
                { icon: CheckCircle2, text: 'Personalized mentoring' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-center space-x-4 group">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center group-hover:shadow-lg group-hover:shadow-[#00E5FF]/50 transition-all duration-300">
                      <Icon className="w-6 h-6 text-[#0B0F1A]" />
                    </div>
                    <p className="text-lg text-[#F9FAFB] font-medium">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Side - Form */}
          <Card className="shadow-2xl shadow-[#00E5FF]/10 border-2 border-[#00E5FF]/30 bg-[#0B0F1A] hover:border-[#00E5FF]/50 transition-all duration-300">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-[#F9FAFB]">Full Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF] focus:ring-[#00E5FF]"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#F9FAFB]">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF] focus:ring-[#00E5FF]"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="mobile" className="text-[#F9FAFB]">Mobile Number *</Label>
                  <Input
                    id="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                    required
                    className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF] focus:ring-[#00E5FF]"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>

                <div>
                  <Label htmlFor="currentStatus" className="text-[#F9FAFB]">Current Status *</Label>
                  <Select value={formData.currentStatus} onValueChange={(value) => handleChange('currentStatus', value)} required>
                    <SelectTrigger className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] focus:border-[#00E5FF] focus:ring-[#00E5FF]">
                      <SelectValue placeholder="Select your status" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#00E5FF]/30">
                      <SelectItem value="student" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">Student</SelectItem>
                      <SelectItem value="fresher" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">Fresher</SelectItem>
                      <SelectItem value="professional" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">Working Professional</SelectItem>
                      <SelectItem value="career-gap" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">Career Gap</SelectItem>
                      <SelectItem value="career-change" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">Career Change</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="interestedDomain" className="text-[#F9FAFB]">Interested Domain *</Label>
                  <Select value={formData.interestedDomain} onValueChange={(value) => handleChange('interestedDomain', value)} required>
                    <SelectTrigger className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] focus:border-[#00E5FF] focus:ring-[#00E5FF]">
                      <SelectValue placeholder="Select domain" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#00E5FF]/30">
                      <SelectItem value="cloud" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">Cloud Computing</SelectItem>
                      <SelectItem value="ai-ml" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">AI & Machine Learning</SelectItem>
                      <SelectItem value="devops" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">DevOps</SelectItem>
                      <SelectItem value="fullstack" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">Full Stack Development</SelectItem>
                      <SelectItem value="data-science" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">Data Science</SelectItem>
                      <SelectItem value="cybersecurity" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">Cybersecurity</SelectItem>
                      <SelectItem value="other" className="text-[#F9FAFB] focus:bg-[#00E5FF]/20">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#F9FAFB]">Message (Optional)</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="mt-2 bg-[#020617] border-[#1F2937] text-[#F9FAFB] placeholder:text-[#9CA3AF] focus:border-[#00E5FF] focus:ring-[#00E5FF]"
                    rows={4}
                    placeholder="Tell us about your career goals..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold py-6 text-lg rounded-lg shadow-lg shadow-[#00E5FF]/40 hover:shadow-[#00E5FF]/60 transition-all duration-300"
                >
                  {isSubmitting ? 'Submitting...' : 'Book Free Consultation'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
    </>
  );
};

export default CareerConsultation;
